import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { Trash2, Download, Save, Share2, Link as LinkIcon, Copy, QrCode } from "@/lib/icons";
import QRCode from "qrcode";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { PRESENTATION_RUBRIC, PROJECT_TOPICS } from "@/data/endOfYearProject";

const STORAGE_KEY = "elementpad:rubric-scores:v1";
const MAX_SCORE = 5;
const SCORES = [0, 1, 2, 3, 4, 5];

const entrySchema = z.object({
  student: z.string().trim().nonempty("Student name is required").max(80, "Max 80 characters"),
  topicId: z.string().trim().nonempty("Pick a topic"),
  scores: z.record(z.string(), z.number().min(0).max(MAX_SCORE)),
  notes: z.string().trim().max(500, "Max 500 characters").optional(),
});

interface SavedEntry {
  id: string;
  student: string;
  topicId: string;
  topicTitle: string;
  scores: Record<string, number>;
  total: number;
  max: number;
  notes?: string;
  savedAt: string;
}

function loadEntries(): SavedEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function emptyScores(): Record<string, number> {
  return PRESENTATION_RUBRIC.reduce<Record<string, number>>((acc, r) => {
    acc[r.label] = 0;
    return acc;
  }, {});
}

export default function RubricScoringForm() {
  const { toast } = useToast();
  const [student, setStudent] = useState("");
  const [topicId, setTopicId] = useState<string>("");
  const [scores, setScores] = useState<Record<string, number>>(() => emptyScores());
  const [notes, setNotes] = useState("");
  const [entries, setEntries] = useState<SavedEntry[]>([]);
  const [shareEntry, setShareEntry] = useState<SavedEntry | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);

  useEffect(() => {
    setEntries(loadEntries());
  }, []);

  const max = PRESENTATION_RUBRIC.length * MAX_SCORE;
  const total = useMemo(
    () => Object.values(scores).reduce((s, n) => s + n, 0),
    [scores],
  );

  function handleSave() {
    const result = entrySchema.safeParse({ student, topicId, scores, notes });
    if (!result.success) {
      toast({
        title: "Check the form",
        description: result.error.issues[0]?.message ?? "Invalid input",
        variant: "destructive",
      });
      return;
    }
    const topic = PROJECT_TOPICS.find((t) => t.id === topicId);
    const entry: SavedEntry = {
      id: crypto.randomUUID(),
      student: result.data.student,
      topicId: result.data.topicId,
      topicTitle: topic?.title ?? result.data.topicId,
      scores: { ...scores },
      total,
      max,
      notes: result.data.notes || undefined,
      savedAt: new Date().toISOString(),
    };
    const next = [entry, ...entries];
    setEntries(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    toast({ title: "Score saved", description: `${entry.student}: ${total}/${max}` });
    // reset
    setStudent("");
    setTopicId("");
    setScores(emptyScores());
    setNotes("");
  }

  function handleDelete(id: string) {
    const next = entries.filter((e) => e.id !== id);
    setEntries(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }

  function handleClearAll() {
    if (!entries.length) return;
    if (!confirm("Delete all saved scores? This cannot be undone.")) return;
    setEntries([]);
    localStorage.removeItem(STORAGE_KEY);
  }

  function buildSummary(e: SavedEntry): string {
    const lines: string[] = [];
    lines.push(`ElementPad - Presentation Rubric`);
    lines.push(`Student: ${e.student}`);
    lines.push(`Topic: ${e.topicTitle}`);
    lines.push(`Score: ${e.total}/${e.max} (${Math.round((e.total / e.max) * 100)}%)`);
    lines.push("");
    PRESENTATION_RUBRIC.forEach((r) => {
      lines.push(`- ${r.label}: ${e.scores[r.label] ?? 0}/${MAX_SCORE}`);
    });
    if (e.notes) {
      lines.push("");
      lines.push(`Notes: ${e.notes}`);
    }
    lines.push("");
    lines.push(`Saved ${new Date(e.savedAt).toLocaleString()}`);
    return lines.join("\n");
  }

  function buildShareLink(e: SavedEntry): string {
    const payload = {
      s: e.student,
      t: e.topicId,
      tt: e.topicTitle,
      sc: e.scores,
      n: e.notes,
      at: e.savedAt,
    };
    const json = JSON.stringify(payload);
    // base64url-encode
    const b64 = btoa(unescape(encodeURIComponent(json)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    return `${window.location.origin}/teach/end-of-year-project?score=${b64}`;
  }

  async function copyToClipboard(text: string, successMessage: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: successMessage });
    } catch {
      toast({
        title: "Copy failed",
        description: "Select the text and copy it manually.",
        variant: "destructive",
      });
    }
  }

  async function handleQuickShare(e: SavedEntry) {
    const summary = buildSummary(e);
    const link = buildShareLink(e);
    const shareText = `${summary}\n\nView: ${link}`;
    if (typeof navigator !== "undefined" && (navigator as Navigator).share) {
      try {
        await (navigator as Navigator).share({
          title: `${e.student} - ${e.topicTitle}`,
          text: summary,
          url: link,
        });
        return;
      } catch {
        // user cancelled or unsupported - fall through to clipboard
      }
    }
    await copyToClipboard(shareText, "Summary and link copied");
  }

  async function handleGenerateQr(e: SavedEntry) {
    setQrError(null);
    try {
      const url = buildShareLink(e);
      // QR codes have ~2.9 KB capacity at error-correction level L; long
      // URLs (lots of notes) can overflow. Surface a friendly message.
      const dataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: "M",
        margin: 1,
        scale: 8,
        color: { dark: "#0f172a", light: "#ffffff" },
      });
      setQrDataUrl(dataUrl);
    } catch {
      setQrDataUrl(null);
      setQrError(
        "This result is too long to fit in a QR code. Try shortening the notes.",
      );
    }
  }

  function handleDownloadQr(e: SavedEntry) {
    if (!qrDataUrl) return;
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = `rubric-${e.student.replace(/\s+/g, "-").toLowerCase()}.png`;
    a.click();
  }

  function handleExportCsv() {
    if (!entries.length) return;
    const headers = ["Saved at", "Student", "Topic", ...PRESENTATION_RUBRIC.map((r) => r.label), "Total", "Max", "Notes"];
    const rows = entries.map((e) => [
      e.savedAt,
      e.student,
      e.topicTitle,
      ...PRESENTATION_RUBRIC.map((r) => String(e.scores[r.label] ?? 0)),
      String(e.total),
      String(e.max),
      e.notes ?? "",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rubric-scores-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Rubric scoring</CardTitle>
        <CardDescription>
          Score each presentation category from 0 to {MAX_SCORE}. Saved locally on this device.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Identity */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="rubric-student">Student name</Label>
            <Input
              id="rubric-student"
              value={student}
              onChange={(e) => setStudent(e.target.value)}
              placeholder="e.g. Alex Rivera"
              maxLength={80}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rubric-topic">Topic</Label>
            <Select value={topicId} onValueChange={setTopicId}>
              <SelectTrigger id="rubric-topic">
                <SelectValue placeholder="Choose a topic" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_TOPICS.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.number.toString().padStart(2, "0")}. {t.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-3">
          {PRESENTATION_RUBRIC.map((r) => (
            <div
              key={r.label}
              className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 sm:gap-4 items-start sm:items-center border border-border rounded-md p-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{r.label}</p>
                <p className="text-xs text-muted-foreground">{r.detail}</p>
              </div>
              <div
                role="radiogroup"
                aria-label={`Score for ${r.label}`}
                className="flex gap-1"
              >
                {SCORES.map((n) => {
                  const selected = scores[r.label] === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setScores((s) => ({ ...s, [r.label]: n }))}
                      className={`h-8 w-8 rounded-md text-sm font-mono border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                        selected
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-foreground border-border hover:border-primary/50"
                      }`}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Notes */}
        <div className="space-y-1.5">
          <Label htmlFor="rubric-notes">Notes (optional)</Label>
          <Textarea
            id="rubric-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Strengths, areas to improve, follow-up..."
            maxLength={500}
            rows={3}
          />
          <p className="text-xs text-muted-foreground">{notes.length}/500</p>
        </div>

        {/* Total + actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-border">
          <div className="text-sm">
            <span className="text-muted-foreground">Total:</span>{" "}
            <span className="font-mono font-semibold text-foreground">
              {total}/{max}
            </span>
            <span className="text-muted-foreground ml-2">
              ({Math.round((total / max) * 100)}%)
            </span>
          </div>
          <Button onClick={handleSave} className="gap-1.5">
            <Save size={14} aria-hidden="true" />
            Save score
          </Button>
        </div>

        {/* Saved entries */}
        {entries.length > 0 && (
          <div className="space-y-2 pt-3 border-t border-border">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                Saved scores ({entries.length})
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleExportCsv} className="gap-1.5">
                  <Download size={14} aria-hidden="true" />
                  CSV
                </Button>
                <Button size="sm" variant="ghost" onClick={handleClearAll}>
                  Clear all
                </Button>
              </div>
            </div>
            <ul className="space-y-2">
              {entries.map((e) => (
                <li
                  key={e.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">
                      {e.student}{" "}
                      <span className="text-muted-foreground font-normal">- {e.topicTitle}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(e.savedAt).toLocaleString()} - {e.total}/{e.max} (
                      {Math.round((e.total / e.max) * 100)}%)
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => handleQuickShare(e)}
                      className="gap-1.5"
                      aria-label={`Share score for ${e.student}`}
                    >
                      <Share2 size={14} aria-hidden="true" />
                      Share
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setShareEntry(e)}
                      aria-label={`Share options for ${e.student}`}
                    >
                      <LinkIcon size={14} aria-hidden="true" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(e.id)}
                      aria-label={`Delete score for ${e.student}`}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>

      <Dialog
        open={!!shareEntry}
        onOpenChange={(o) => {
          if (!o) {
            setShareEntry(null);
            setQrDataUrl(null);
            setQrError(null);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Share rubric result</DialogTitle>
            <DialogDescription>
              {shareEntry
                ? `${shareEntry.student} - ${shareEntry.topicTitle}`
                : ""}
            </DialogDescription>
          </DialogHeader>
          {shareEntry && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="share-summary">Summary</Label>
                <Textarea
                  id="share-summary"
                  readOnly
                  rows={9}
                  value={buildSummary(shareEntry)}
                  className="font-mono text-xs"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 w-full"
                  onClick={() =>
                    copyToClipboard(buildSummary(shareEntry), "Summary copied")
                  }
                >
                  <Copy size={14} aria-hidden="true" />
                  Copy summary
                </Button>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="share-link">Share link</Label>
                <Input
                  id="share-link"
                  readOnly
                  value={buildShareLink(shareEntry)}
                  className="font-mono text-xs"
                  onFocus={(ev) => ev.currentTarget.select()}
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 w-full"
                  onClick={() =>
                    copyToClipboard(buildShareLink(shareEntry), "Link copied")
                  }
                >
                  <LinkIcon size={14} aria-hidden="true" />
                  Copy link
                </Button>
                <p className="text-xs text-muted-foreground">
                  Anyone with the link can view this result on the End-of-Year Project page.
                  Scores are encoded in the URL - nothing is uploaded.
                </p>
              </div>

              <div className="space-y-2 pt-3 border-t border-border">
                <div className="flex items-center justify-between gap-2">
                  <Label>QR code</Label>
                  {!qrDataUrl && (
                    <Button
                      size="sm"
                      onClick={() => handleGenerateQr(shareEntry)}
                      className="gap-1.5"
                    >
                      <QrCode size={14} aria-hidden="true" />
                      Generate QR
                    </Button>
                  )}
                </div>
                {qrError && (
                  <p className="text-xs text-destructive">{qrError}</p>
                )}
                {qrDataUrl && (
                  <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-white p-4">
                    <img
                      src={qrDataUrl}
                      alt={`QR code linking to rubric result for ${shareEntry.student}`}
                      width={192}
                      height={192}
                      className="h-48 w-48"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => handleDownloadQr(shareEntry)}
                    >
                      <Download size={14} aria-hidden="true" />
                      Download PNG
                    </Button>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Privacy first: the QR is generated on your device and encodes the same
                  share link above. Nothing is uploaded to a server.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
